import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Download, FileText, FileSpreadsheet, FileType, X } from 'lucide-react';

function InvoiceContextMenu({ 
  invoice, 
  position, 
  onClose, 
  onDelete, 
  onDownload 
}) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position to keep menu in viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 220),
    y: Math.min(position.y, window.innerHeight - 280)
  };

  const menuItems = [
    {
      type: 'header',
      label: 'Download As'
    },
    {
      icon: FileSpreadsheet,
      label: 'Excel (.xlsx)',
      onClick: () => { onDownload(invoice, 'xlsx'); onClose(); },
      color: 'text-emerald-600'
    },
    {
      icon: FileText,
      label: 'PDF (.pdf)',
      onClick: () => { onDownload(invoice, 'pdf'); onClose(); },
      color: 'text-rose-600'
    },
    {
      icon: FileType,
      label: 'Word (.docx)',
      onClick: () => { onDownload(invoice, 'docx'); onClose(); },
      color: 'text-blue-600'
    },
    {
      icon: FileText,
      label: 'Text (.txt)',
      onClick: () => { onDownload(invoice, 'txt'); onClose(); },
      color: 'text-slate-600'
    },
    {
      type: 'divider'
    },
    {
      icon: Trash2,
      label: 'Delete Invoice',
      onClick: () => { onDelete(invoice); onClose(); },
      color: 'text-rose-600',
      hoverBg: 'hover:bg-rose-50'
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.1 }}
        className="fixed z-50 bg-white rounded-xl shadow-xl border border-slate-200 py-2 w-52 overflow-hidden"
        style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
        data-testid="invoice-context-menu"
      >
        {/* Invoice info header */}
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
          <p className="text-xs font-medium text-slate-500 truncate">
            {invoice.extracted_data?.invoice_number || invoice.filename}
          </p>
        </div>

        {menuItems.map((item, index) => {
          if (item.type === 'header') {
            return (
              <div key={index} className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {item.label}
              </div>
            );
          }
          
          if (item.type === 'divider') {
            return <div key={index} className="my-1 border-t border-slate-100" />;
          }

          const Icon = item.icon;
          return (
            <button
              key={index}
              onClick={item.onClick}
              className={`w-full px-3 py-2 flex items-center space-x-3 hover:bg-slate-50 transition-colors ${item.hoverBg || ''}`}
              data-testid={`context-menu-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <Icon className={`w-4 h-4 ${item.color}`} />
              <span className="text-sm text-slate-700">{item.label}</span>
            </button>
          );
        })}
      </motion.div>
    </AnimatePresence>
  );
}

export default InvoiceContextMenu;
